import type { GameState } from './gameState';
import { getCrop, CROPS, cropInSeason } from '../data/crops';
import { tickCropDay } from '../sim/crops';
import { settleShipping } from '../sim/economy';
import { restoreEnergyFromSleep } from '../sim/energy';
import { wakeClock } from '../sim/time';
import { seasonOf } from '../sim/seasons';

export interface DayRolloverSummary {
  earned: number;
  newDay: number;
  cropsAdvanced: number;
}

/** The crop produce that sells at a bonus today (rotates daily). */
export function hotCropItem(day: number): string {
  const list = Object.values(CROPS);
  return list[day % list.length]!.produceItemId;
}

/**
 * Pure day rollover used by sleep AND the forced-2am collapse:
 *   1. pay out the shipping bin
 *   2. grow watered crops one day, clear watered flags
 *   3. restore energy (less if forced)
 *   4. advance to the next morning
 * Returns a NEW state plus a summary for the wake-up screen. Fully testable.
 */
export function runDayRollover(
  state: GameState,
  forced: boolean,
): { state: GameState; summary: DayRolloverSummary } {
  const settled = settleShipping(state.money, state.shippingBin, hotCropItem(state.clock.day));

  let cropsAdvanced = 0;
  const season = seasonOf(state.clock.day).id;
  const crops = state.crops.map((c) => {
    const def = getCrop(c.cropId);
    if (!def) return c;
    const next = tickCropDay(c, def, cropInSeason(c.cropId, season) ? 1 : 0.5); // off-season crops grow slower
    if (next.growth !== c.growth) cropsAdvanced += 1;
    return next;
  });

  const clock = wakeClock(state.clock);
  const energy = restoreEnergyFromSleep(forced, state.energyMax);

  const next: GameState = {
    ...state,
    money: settled.money,
    shippingBin: settled.bin,
    crops,
    clock,
    energy,
    stats: {
      ...state.stats,
      daysPlayed: state.stats.daysPlayed + 1,
      totalEarned: state.stats.totalEarned + settled.earned,
    },
  };

  return { state: next, summary: { earned: settled.earned, newDay: clock.day, cropsAdvanced } };
}
